import { useEffect, useState } from "react";

/* God Console API client → clicky-proxy worker. Dev uses the /api proxy; prod
   hits the worker directly. Auth is the same Adaptr JWT, gated to super-admins. */
const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN as string) || "https://clicky-proxy.air.workers.dev";
const API_BASE = import.meta.env.DEV ? "/api" : API_ORIGIN;

const ACCESS_KEY = "adaptr_god_token";
const REFRESH_KEY = "adaptr_god_refresh";
export const getToken = () => localStorage.getItem(ACCESS_KEY);
const getRefresh = () => localStorage.getItem(REFRESH_KEY);
export const isAuthed = () => !!getToken();
function setTokens(access: string, refresh?: string) {
  localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

/** Capture the WorkOS session from the URL fragment (#access=…&refresh=…) on boot. */
export function captureSessionFromHash() {
  if (!location.hash.startsWith("#")) return;
  const params = new URLSearchParams(location.hash.slice(1));
  const access = params.get("access");
  if (access) {
    setTokens(access, params.get("refresh") ?? undefined);
    history.replaceState(null, "", location.pathname + location.search);
  }
}

export function login() {
  location.href = `${API_ORIGIN}/auth/login?client=god`;
}
export function logout() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  location.href = "/";
}

async function rawFetch(path: string, init: RequestInit, token: string | null) {
  return fetch(API_BASE + path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(init.headers || {}) },
  });
}

async function tryRefresh(): Promise<string | null> {
  const refresh = getRefresh();
  if (!refresh) return null;
  const res = await rawFetch("/auth/refresh", { method: "POST", body: JSON.stringify({ refresh }) }, null);
  if (!res.ok) return null;
  const data = (await res.json()) as { access?: string; refresh?: string };
  if (!data.access) return null;
  setTokens(data.access, data.refresh);
  return data.access;
}

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}`);
  }
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await rawFetch(path, init, getToken());
  if (res.status === 401 && getRefresh()) {
    const fresh = await tryRefresh();
    if (fresh) res = await rawFetch(path, init, fresh);
  }
  if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => ""));
  return (await res.json()) as T;
}

// ---- super-admin gate -------------------------------------------------------
export type AuthState = { status: "loading" | "anon" | "forbidden" | "ok"; email?: string };

/** Confirms the operator is a platform super-admin via GET /admin/whoami. */
export function useSuperAdmin(): AuthState {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  useEffect(() => {
    if (!isAuthed()) {
      setState({ status: "anon" });
      return;
    }
    let alive = true;
    api<{ email: string; superAdmin: boolean }>("/admin/whoami")
      .then((d) => alive && setState({ status: "ok", email: d.email }))
      .catch((e) => {
        if (!alive) return;
        setState({ status: e instanceof ApiError && e.status === 403 ? "forbidden" : "anon" });
      });
    return () => {
      alive = false;
    };
  }, []);
  return state;
}
